import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
  import { useParams } from "react-router-dom";
import api from "../services/api";
import { getToken } from '../utils/auth';
import Modal from "../components/Modal"; // Assuming you have this Modal component
import {
  // ArrowLeftIcon,
  PaperAirplaneIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingInIcon,
  QuestionMarkCircleIcon, // For Raise Query button
  ChatBubbleLeftRightIcon, // For displaying queries
  
} from "@heroicons/react/24/outline";
import { toastError, toastSuccess } from "../utils/hotToast";
// toastInfo available but not currently used

// Import react-pdf components
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css"; // Essential for annotations like links
import "react-pdf/dist/Page/TextLayer.css"; // Essential for selectable text
// Worker is configured once in index.js

export default function StudentCopyViewer() {
  const { copyId } = useParams();

  // State for copy data and UI
  const [copy, setCopy] = useState(null);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1); // Current page of the Answer Copy
  const [qpCurrentPage, setQpCurrentPage] = useState(1); // Current page of the Question Paper
  const [studentQueries, setStudentQueries] = useState([]); // NEW: State for student's queries on this copy

  // Toasts handled via `react-hot-toast` helpers

  // Zoom States
  const [qpZoomLevel, setQpZoomLevel] = useState(1.25);
  console.log("QP Zoom Level:", qpZoomLevel);
  const [acZoomLevel, setAcZoomLevel] = useState(1.25);

  const ZOOM_STEP = 0.25;
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;

  // Query Modal States
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [queryPage, setQueryPage] = useState("");
  const [queryText, setQueryText] = useState("");
  const [isSubmittingQuery, setIsSubmittingQuery] = useState(false);

  // New states for react-pdf to track total pages
  // eslint-disable-next-line no-unused-vars
  const [numQpPages, setNumQpPages] = useState(null);
  console.log("Number of QP Pages:", numQpPages);
  const [numAcPages, setNumAcPages] = useState(null);

  // Initial loading state for the entire component's data
  const [isLoadingComponent, setIsLoadingComponent] = useState(true);

  // Callback for successful PDF document load (not per page load)
  // const onQpDocumentLoadSuccess = useCallback(({ numPages }) => {
  //   setNumQpPages(numPages);
  // }, []);

  // Screenshot and Print Protection
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Block Print Screen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        toastError("Screenshots are not allowed for security reasons.");
        return false;
      }

      // Block Ctrl+P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        toastError("Printing is disabled for security reasons.");
        return false;
      }

      // Block Ctrl+S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        toastError("Saving is not allowed for security reasons.");
        return false;
      }

      // Block F12 (Developer Tools)
      if (e.key === "F12") {
        e.preventDefault();
        toastError("Developer tools are disabled for security reasons.");
        return false;
      }

      // Block Ctrl+Shift+I (Developer Tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "i") {
        e.preventDefault();
        toastError("Developer tools are disabled for security reasons.");
        return false;
      }

      // Block Ctrl+Shift+C (Element Inspector)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "c") {
        e.preventDefault();
        toastError("Developer tools are disabled for security reasons.");
        return false;
      }

      // Block Ctrl+Shift+J (Console)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "j") {
        e.preventDefault();
        toastError("Developer tools are disabled for security reasons.");
        return false;
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      toastError("Right-click is disabled for security reasons.");
      return false;
    };

    const handleCopy = (e) => {
      e.preventDefault();
      toastError("Copying content is not allowed for security reasons.");
      return false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", handleCopy);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", handleCopy);
    };
  }, []);

  const onAcDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumAcPages(numPages);
  }, []);

  const pageWrapperRef = useRef(null);

  // 1) Load the copy and its queries once on mount
  useEffect(() => {
    const fetchCopyAndQueries = async () => {
      setIsLoadingComponent(true); // Start loading for the component
      try {
        const copyRes = await api.get(`/student/copies/${copyId}`);
        setCopy(copyRes.data);
        setCurrentPage(1);
        setQpCurrentPage(1);

        const queriesRes = await api.get(`/student/queries?copyId=${copyId}`);
        setStudentQueries(queriesRes.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        toastError(`Error loading copy or queries: ${err.response?.data?.message || err.message}`);
      } finally {
        setIsLoadingComponent(false); // End loading for the component
      }
    };
    fetchCopyAndQueries();
  }, [copyId]);

  // 2) Reset zoom when current page of answer copy changes
  useEffect(() => {
    setAcZoomLevel(1.25);
  }, [currentPage]);

  // 3) Reset zoom when current page of question paper changes
  useEffect(() => {
    setQpZoomLevel(1.25);
  }, [qpCurrentPage]);

  // Toasts handled via `react-hot-toast` helpers

  // Handler for zooming images
  const handleZoom = (type, action) => {
    if (type === "qp") {
      setQpZoomLevel((prevZoom) => {
        let newZoom = prevZoom;
        if (action === "in") {
          newZoom = Math.min(MAX_ZOOM, prevZoom + ZOOM_STEP);
        } else if (action === "out") {
          newZoom = Math.max(MIN_ZOOM, prevZoom - ZOOM_STEP);
        } else if (action === "reset") {
          newZoom = 1.25;
        }
        return parseFloat(newZoom.toFixed(2));
      });
    } else if (type === "ac") {
      setAcZoomLevel((prevZoom) => {
        let newZoom = prevZoom;
        if (action === "in") {
          newZoom = Math.min(MAX_ZOOM, prevZoom + ZOOM_STEP);
        } else if (action === "out") {
          newZoom = Math.max(MIN_ZOOM, prevZoom - ZOOM_STEP);
        } else if (action === "reset") {
          newZoom = 1.25;
        }
        return parseFloat(newZoom.toFixed(2));
      });
    }
  };

  // Query Modal functions
  const openQueryForm = () => {
    setQueryPage(currentPage.toString()); // Pre-fill with current AC page
    setQueryText("");
    setIsQueryModalOpen(true);
  };

  const closeQueryModal = () => {
    setIsQueryModalOpen(false);
  };

  const submitQuery = async () => {
    if (!copy) return;

    if (
      !queryPage ||
      isNaN(Number(queryPage)) ||
      Number(queryPage) <= 0 ||
      Number(queryPage) > copy.totalPages
    ) {
      toastError(`Please enter a valid page number between 1 and ${copy.totalPages}.`);
      return;
    }
    if (!queryText.trim()) {
      toastError("Query text cannot be empty.");
      return;
    }

    // Check if a query already exists for this page (regardless of status)
    const existingQueryForPage = studentQueries.find(
      (q) => q.pageNumber === Number(queryPage)
    );
    if (existingQueryForPage) {
      toastError(`You have already raised a query for page ${queryPage}. Only one query per page is allowed.`);
      closeQueryModal();
      return;
    }

    setIsSubmittingQuery(true);
    try {
      await api.post(`/student/copies/${copyId}/queries`, {
        pageNumber: Number(queryPage),
        text: queryText.trim(),
      });
      toastSuccess("Query submitted successfully!");
      closeQueryModal();
      // Re-fetch queries after submission to update the list
      const updatedQueriesRes = await api.get(
        `/student/queries?copyId=${copyId}`
      );
      setStudentQueries(updatedQueriesRes.data);
    } catch (err) {
      toastError(`Error submitting query: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSubmittingQuery(false);
    }
  };

  // Memoized stable file props — must be before any early returns (hooks rules)
  const acPdfUrl = copy?.driveFile?.id ? `/api/drive/pdf/${copy.driveFile.id}` : '';
  const qpPdfUrl = copy?.questionPaper?.driveFile?.id ? `/api/drive/pdf/${copy.questionPaper.driveFile.id}` : '';
  const acFileProp = useMemo(
    () => acPdfUrl ? { url: acPdfUrl, httpHeaders: { Authorization: `Bearer ${getToken()}` } } : null,
    [acPdfUrl] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [loadingPdfs, setLoadingPdfs] = useState(new Set());

  // Early returns for loading and error states
  if (error)
    return (
      <div className="text-red-500 text-center py-10 text-xl font-semibold">
        Error: {error}
      </div>
    );
  if (isLoadingComponent || !copy)
    // Use isLoadingComponent for initial data fetch
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <div className="flex flex-col items-center text-gray-600 text-lg font-bold">
          <svg
            className="animate-spin h-10 w-10 text-gray-900"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="mt-4">Loading copy details...</p>
        </div>
      </div>
    );

  // Get current page data for Answer Copy
  const currentAcPageData = copy.pages.find(
    (p) => p.pageNumber === currentPage
  );
  const marksAwarded = currentAcPageData?.marksAwarded ?? "N/A";
  const comments = currentAcPageData?.comments?.join("\n") || "No comments.";

  // Calculate total marks (sum of marksAwarded from all pages)
  const totalMarks = copy.pages.reduce(
    (sum, p) => sum + (Number(p.marksAwarded) || 0),
    0
  );

  const openPdfWithAuth = async (url) => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) return;
    newWindow.document.write('<p style="font-family:sans-serif;padding:20px;color:#666;">Loading PDF…</p>');
    setLoadingPdfs(prev => new Set(prev).add(url));
    try {
      const axiosPath = url.replace(/^\/api/, '');
      const response = await api.get(axiosPath, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      newWindow.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Failed to open PDF:', err);
      newWindow.close();
    } finally {
      setLoadingPdfs(prev => { const s = new Set(prev); s.delete(url); return s; });
    }
  };

  return (
    <div 
      className="bg-white min-h-screen font-sans relative" 
      style={{
        fontFamily: 'Dosis, sans-serif',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
      onDragStart={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      {/* Toasts shown via global Toaster (react-hot-toast) */}

      {/* Top Navigation Bar */}

      <div className="p-4">
        {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
        <h1 className="text-4xl font-extrabold text-gray-900 mb-6 text-center tracking-tight"></h1>

        {/* Total Marks Display */}
        <div className="mx-auto mb-8 text-center">
          <span className="text-gray-900 font-bold">{copy.questionPaper.title}</span>
          <h3 className="text-2xl font-bold text-gray-900 mt-2">
            Your Total Marks
          </h3>
          <div className="flex items-center justify-center gap-3 mt-2">
            <span className="text-4xl font-extrabold text-gray-900">{totalMarks}</span>
            <span className="text-2xl text-gray-500">/</span>
            <div className="flex flex-col items-start">
              <span className="text-sm text-gray-600 font-medium">Maximum Marks</span>
              <span className="text-2xl font-bold text-gray-900">{copy.questionPaper.totalMarks}</span>
            </div>
          </div>
          <div className="mt-2">
            <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold ${
              totalMarks >= copy.questionPaper.totalMarks * 0.75
                ? 'bg-green-100 text-green-800'
                : totalMarks >= copy.questionPaper.totalMarks * 0.5
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {Math.round((totalMarks / copy.questionPaper.totalMarks) * 100)}% Score
            </span>
          </div>
        </div>

        {/* Question Paper Link */}
        <div className=" mb-6">
          {copy.questionPaper?.driveFile?.id ? (
            <button
              onClick={() => openPdfWithAuth(`/api/drive/pdf/${copy.questionPaper.driveFile.id}`)}
              disabled={loadingPdfs.has(`/api/drive/pdf/${copy.questionPaper.driveFile.id}`)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: loadingPdfs.has(`/api/drive/pdf/${copy.questionPaper.driveFile.id}`) ? 'not-allowed' : 'pointer' }}
              className="inline-flex items-center disabled:opacity-60"
            >
              {loadingPdfs.has(`/api/drive/pdf/${copy.questionPaper.driveFile.id}`) ? (
                <><svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>Loading...</>
              ) : (
                <><svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>Open Question Paper in New Tab</>
              )}
            </button>
          ) : (
            <div className="text-gray-600">
              Question paper link not available.
            </div>
          )}
        </div>

        {/* Answer Copy and Evaluation Section - Side by Side */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {/* Left: Answer Copy Section */}
          <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col lg:w-[80%]">
            <div className="relative w-full flex-grow h-[calc(100vh-250px)] rounded-lg overflow-auto border-2 border-gray-300 bg-gray-50 flex items-center justify-center">
              {/* React-PDF Document and Page for Answer Copy */}
              {acPdfUrl ? (
                <div className="relative inline-block" ref={pageWrapperRef}>
                  <Document
                    file={acFileProp}
                    onLoadSuccess={onAcDocumentLoadSuccess}
                    onLoadError={(err) => {
                      console.error("Error loading AC PDF:", err);
                      setError("Failed to load Answer Copy PDF.");
                    }}
                    loading={
                      <div className="flex flex-col items-center justify-center">
                        <svg
                          className="animate-spin h-10 w-10 text-gray-900"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <p className="mt-3 text-gray-600">Loading PDF...</p>
                      </div>
                    }
                    className="w-full h-full flex items-center justify-center"
                  >
                    <Page
                      pageNumber={currentPage}
                      scale={acZoomLevel}
                      renderAnnotationLayer={true}
                      renderTextLayer={true}
                      loading={
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin h-8 w-8 text-gray-900"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                      }
                    />
                  </Document>

                  {/* Render examiner's marks on the current page (read-only) */}
                  {copy && copy.pages && Array.isArray(copy.pages) && (() => {
                    const pageData = copy.pages.find(p => p && p.pageNumber === currentPage);
                    if (pageData && pageData.pageMarks && Array.isArray(pageData.pageMarks) && pageData.pageMarks.length > 0) {
                      return pageData.pageMarks
                        .filter(mark => mark && typeof mark.value === 'number' && typeof mark.x === 'number' && typeof mark.y === 'number')
                        .map((mark, idx) => (
                        <div
                          key={idx}
                          className="absolute pointer-events-none"
                          style={{
                            left: `${mark.x}%`,
                            top: `${mark.y}%`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: 20,
                            position: 'absolute'
                          }}
                        >
                          <div className={`w-12 h-12 flex items-center justify-center rounded-full text-white text-sm font-bold shadow-lg ${mark.value > 0 ? 'bg-green-500' : 'bg-red-500'}`}>
                            {Number(mark.value % 1 === 0 ? mark.value : mark.value.toFixed(1))}
                          </div>
                        </div>
                      ));
                    }
                    return null;
                  })()}
                </div>
              ) : (
                <div className="text-gray-500 text-center p-4">
                  Answer Copy PDF Not Found.
                </div>
              )}
              
              {/* Marks are rendered inside the page wrapper for correct positioning */}
            </div>
            <div className="flex items-center justify-center mt-4 space-x-2">
              <button
                onClick={() => handleZoom("ac", "out")}
                disabled={acZoomLevel === MIN_ZOOM}
                className="p-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Zoom Out"
              >
                <MagnifyingGlassMinusIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleZoom("ac", "in")}
                disabled={acZoomLevel === MAX_ZOOM}
                className="p-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Zoom In"
              >
                <MagnifyingGlassPlusIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleZoom("ac", "reset")}
                disabled={acZoomLevel === MIN_ZOOM}
                className="p-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Reset Zoom"
              >
                <ArrowsPointingInIcon className="h-5 w-5" />
              </button>
              <span className="text-sm text-gray-600 font-bold">
                {acZoomLevel.toFixed(2)}x
              </span>
            </div>
          </div>

          {/* Right: Evaluation Section with Page Navigation */}
          <div className="bg-white p-4 rounded-xl border border-gray-200 lg:w-[20%] flex flex-col">
            {/* Page Navigation */}
            {numAcPages > 1 && (
              <div className="flex flex-col gap-2 mb-4 pb-4 border-b">
                <div className="flex justify-between items-center space-x-2">
                  <button
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={currentPage === 1}
                    className="flex px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed transition font-bold text-sm"
                  >
                    Prev
                  </button>
                  <span className="text-center text-base font-bold text-gray-900">
                    Page {currentPage} / {numAcPages || "..."}
                  </span>
                  <button
                    onClick={() => {
                      setCurrentPage((p) => Math.min(numAcPages, p + 1));
                    }}
                    disabled={currentPage === numAcPages}
                    className="flex px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed transition font-bold text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            <h3 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">
              Evaluation for Page {currentPage}
            </h3>
            <div className="mb-3">
              <label className="block text-sm font-bold text-gray-900 mb-1">
                Marks Awarded:
              </label>
              <p className="w-full px-2 py-1 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 text-sm">
                {marksAwarded}
              </p>
            </div>
            <div className="mb-4 flex-grow">
              <label className="block text-sm font-bold text-gray-900 mb-1">
                Comments:
              </label>
              <p className="w-full px-2 py-1 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 text-sm whitespace-pre-wrap">
                {comments}
              </p>
            </div>
            <button
              onClick={openQueryForm}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-all duration-200 ease-in-out text-base font-bold flex items-center justify-center mt-auto"
            >
              <QuestionMarkCircleIcon className="h-5 w-5 mr-2" /> Raise Query
            </button>
          </div>
        </div>

        {/* NEW: Display Raised Queries Section */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 w-full max-w-full mx-auto mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2 flex items-center">
            <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2 text-gray-600" />{" "}
            Your Raised Queries for this Copy
          </h3>
          {studentQueries.length === 0 ? (
            <p className="text-gray-600 text-center py-4">
              No queries have been raised for this copy yet.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                      Page
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                      Your Query
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                      Response
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                      Action Taken
                    </th>{" "}
                    {/* NEW HEADER */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {studentQueries.map((query) => (
                    <tr key={query._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {query.pageNumber}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {query.text}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            query.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : query.status === "approved_by_admin"
                              ? "bg-blue-100 text-blue-800"
                              : query.status === "rejected_by_admin"
                              ? "bg-red-100 text-red-800"
                              : query.status === "resolved_by_admin"
                              ? "bg-green-100 text-green-800"
                              : query.status === "resolved_by_examiner"
                              ? "bg-purple-100 text-purple-800" // Added for examiner resolution
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {query.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {query.response || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs ">
                        {" "}
                        {/* NEW CELL */}
                        {query.action || "No specific action recorded."}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Raise Query Modal */}
      <Modal
        isOpen={isQueryModalOpen}
        onClose={closeQueryModal}
        title={`Raise Query on: ${copy.questionPaper?.title || "Copy"}`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitQuery();
          }}
          className="space-y-5 p-2"
        >
          <div>
            <label
              htmlFor="queryPage"
              className="block text-gray-900 text-base font-bold mb-2"
            >
              Page Number (1 - {copy.totalPages}):
            </label>
            <input
              id="queryPage"
              type="number"
              min="1"
              max={copy.totalPages}
              value={queryPage}
              onChange={(e) => setQueryPage(e.target.value)}
              className="w-full md:w-32 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-base appearance-none"
              placeholder="e.g., 5"
              required
            />
            {queryPage && studentQueries.find((q) => q.pageNumber === Number(queryPage)) && (
              <p className="mt-2 text-sm text-red-600 font-medium">
                 You already have a query for page {queryPage}. Only one query per page is allowed.
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="queryText"
              className="block text-gray-900 text-base font-bold mb-2"
            >
              Your Query:
            </label>
            <textarea
              id="queryText"
              rows="4"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-base resize-y"
              placeholder="Type your question or concern about the marking on this page..."
              required
            ></textarea>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={closeQueryModal}
              className="px-6 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 font-bold transition duration-150"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmittingQuery}
              className="inline-flex items-center px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 font-bold transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingQuery ? (
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <PaperAirplaneIcon className="h-5 w-5 mr-2" />
              )}
              {isSubmittingQuery ? "Submitting..." : "Submit Query"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
